import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../services/api';
import './ResetPassword.css';

function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const isSelfService = searchParams.get('selfService') === 'true';

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setErrors({ token: 'Invalid or missing reset token' });
      setValidating(false);
      return;
    }

    const validateToken = async () => {
      try {
        await authAPI.validateResetToken(token);
      } catch (error) {
        setErrors({ token: error.response?.data?.error || 'Invalid or expired reset token' });
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must contain uppercase, lowercase, and number';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      await authAPI.resetPassword({
        token,
        password: formData.password
      });
      setSuccess(true);
      setTimeout(() => {
        navigate(isSelfService ? '/picks' : '/login');
      }, 3000);
    } catch (error) {
      console.error('Reset password error:', error);
      setErrors({
        submit: error.response?.data?.error || 'Failed to reset password. The link may have expired.'
      });
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-card">
          <div className="reset-password-header">
            <h1>Validating Reset Link...</h1>
          </div>
        </div>
      </div>
    );
  }

  if (errors.token) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-card">
          <div className="reset-password-header">
            <h1>Invalid Reset Link</h1>
          </div>
          <div className="reset-password-error">
            <p>{errors.token}</p>
            <p>This link may have expired. You can request a new one from the login page using "Forgot Password".</p>
          </div>
          <button onClick={() => navigate('/forgot-password')} className="btn btn-primary">
            Request New Reset Link
          </button>
          <div className="reset-password-footer">
            <button
              type="button"
              onClick={() => navigate(isSelfService ? '/picks' : '/login')}
              className="link-button"
            >
              {isSelfService ? 'Back to Picks' : 'Back to Login'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-card">
          <div className="reset-password-header success">
            <h1>Password Reset Successful!</h1>
          </div>
          <div className="reset-password-success">
            <p>Your password has been successfully reset.</p>
            <p>{isSelfService ? 'Redirecting back to picks...' : 'Redirecting to login page...'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-container">
      <div className="reset-password-card">
        <div className="reset-password-header">
          <h1>Reset Your Password</h1>
          <p>Enter your new password below</p>
        </div>

        <form onSubmit={handleSubmit} className="reset-password-form">
          <div className="form-group">
            <label htmlFor="password">
              New Password <span className="required">*</span>
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className={errors.password ? 'error' : ''}
              placeholder="Enter new password"
              disabled={loading}
            />
            {errors.password && <span className="error-message">{errors.password}</span>}
            <span className="field-hint">
              Must be at least 8 characters with uppercase, lowercase, and number
            </span>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">
              Confirm Password <span className="required">*</span>
            </label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              className={errors.confirmPassword ? 'error' : ''}
              placeholder="Confirm new password"
              disabled={loading}
            />
            {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
          </div>

          {errors.submit && (
            <div className="error-message submit-error">{errors.submit}</div>
          )}

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Resetting Password...' : 'Reset Password'}
          </button>

          <div className="reset-password-footer">
            <button
              type="button"
              onClick={() => navigate(isSelfService ? '/picks' : '/login')}
              className="link-button"
              disabled={loading}
            >
              {isSelfService ? 'Back to Picks' : 'Back to Login'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ResetPassword;
